import {
  calculateWorkerTotal,
  DEFAULT_STANDARD_WORK_DAY_HOURS,
  type RateBasis,
} from '@shared/labour-costing';

/**
 * Actual Labour monetary + worked-duration helpers.
 *
 * Canonical rules used across the web UI and the Worker:
 *   - New rows use daily rates; legacy rows explicitly keep hourly rates.
 *   - Worked time is whole-day minute arithmetic. When Time Out is earlier than
 *     or equal to Time In, it is interpreted as the following calendar day
 *     (overnight wrap). Equal times always produce 0 duration and are rejected.
 *   - Money is computed from raw minutes (never from a rounded hours value),
 *     then rounded to 2 decimals for display/storage.
 */

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Parses "HH:MM" into minutes since midnight; null when malformed or out of range. */
export function normalizeTimeMinutes(time: string | undefined): number | null {
  if (typeof time !== 'string') return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * Whole-day worked minutes for a worker row. Both times are required.
 *
 * - Equal times are always invalid (no zero-length shifts).
 * - When Time Out is earlier than Time In, the shift is treated as overnight
 *   (Time Out is on the following calendar day, e.g. 22:00→04:00 = 360 min).
 * - Duration must not exceed 24 hours (1440 min).
 *
 * Returns null when the range is missing, invalid, or exceeds the maximum.
 */
export function workedMinutes(timeIn: string | undefined, timeOut: string | undefined): number | null {
  const start = normalizeTimeMinutes(timeIn);
  const end = normalizeTimeMinutes(timeOut);
  if (start === null || end === null) return null;
  if (start === end) return null;
  if (end < start) {
    const minutes = (1440 - start) + end;
    return minutes > 1440 ? null : minutes;
  }
  return end - start;
}

/** Whole-day hours rounded to 2 decimals; 0 when the range is missing/invalid. */
export function durationHours(timeIn: string | undefined, timeOut: string | undefined): number {
  const minutes = workedMinutes(timeIn, timeOut);
  return minutes === null ? 0 : round2(minutes / 60);
}

/** Worker Total from worked minutes; null when times are invalid. */
export function workerTotal(
  count: number,
  minutes: number,
  rate: number,
  rateBasis: RateBasis = 'DAILY',
  standardHours = DEFAULT_STANDARD_WORK_DAY_HOURS,
): number | null {
  return calculateWorkerTotal(count, minutes, rate, rateBasis, standardHours);
}

/** Human-readable worked-duration label, e.g. "4 hours 15 minutes", "6 hours". */
export function formatWorkedHours(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) return '—';

  const totalMinutes = Math.round(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  if (hours === 0 && mins === 0) return '0 hours';

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  if (mins > 0) parts.push(`${mins} ${mins === 1 ? 'minute' : 'minutes'}`);
  return parts.join(' ');
}
